import React, { useEffect, useRef, useState } from "react";
import { Navbar, Nav, Container } from "react-bootstrap";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "../siteSettings";
import "./MyNavbar.css";

const SCROLL_HIDE_AFTER = 80;
const SCROLL_DELTA = 8;

/** Up to two initials from the site name, for the no-logo placeholder. */
function initialsOf(name) {
  // Latin letters incl. accents (Á, ñ, ü…) and digits; skips punctuation.
  const words = String(name || "")
    .replace(/[^0-9A-Za-z\u00C0-\u024F\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/** Neutral stand-in when a client has no logo: initials, or a generic ball. */
function LogoPlaceholder({ name }) {
  const initials = initialsOf(name);
  return (
    <span className="navbar-logo-placeholder">
      {initials || (
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 7.5l3.2 2.3-1.2 3.8h-4l-1.2-3.8z" fill="currentColor" />
        </svg>
      )}
    </span>
  );
}

const MyNavbar = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { loaded, siteName, officialLogoUrl, hasConfiguredName } =
    useSiteSettings();
  const lastY = useRef(0);
  const ticking = useRef(false);
  const expandedRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    setExpanded(false);
    expandedRef.current = false;
    setIsVisible(true);
  }, [location.pathname]);

  useEffect(() => {
    expandedRef.current = expanded;
    if (expanded) setIsVisible(true);
  }, [expanded]);

  useEffect(() => {
    lastY.current = window.scrollY;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;

        if (!expandedRef.current) {
          if (y <= SCROLL_HIDE_AFTER) {
            setIsVisible(true);
          } else if (delta > SCROLL_DELTA) {
            setIsVisible(false);
          } else if (delta < -SCROLL_DELTA) {
            setIsVisible(true);
          }
        }

        lastY.current = y;
        ticking.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <Navbar
        expand="md"
        fixed="top"
        variant="dark"
        className={`my-navbar ${
          isVisible ? "navbar-visible" : "navbar-hidden"
        }`}
        expanded={expanded}
        onToggle={setExpanded}
      >
        <Container fluid="lg" className="navbar-inner">
          <Navbar.Brand as={Link} to="/" className="logo-container">
            {/* Fixed square box: the logo can arrive late without shifting the bar */}
            <span className="navbar-logo-box" aria-hidden="true">
              {loaded &&
                (officialLogoUrl ? (
                  <img
                    src={officialLogoUrl}
                    alt=""
                    width="48"
                    height="48"
                    decoding="async"
                    className="navbar-logo"
                  />
                ) : (
                  <LogoPlaceholder name={hasConfiguredName ? siteName : ""} />
                ))}
            </span>
            {loaded ? (
              <span className="brand-text">{siteName}</span>
            ) : (
              // Blank until settings load, so no wrong name ever flashes.
              <span className="brand-text brand-text--pending" aria-hidden="true" />
            )}
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="main-nav"
            className="navbar-toggler-custom"
            label="Abrir menú"
          >
            <span className={`hamburger-icon${expanded ? " open" : ""}`}>
              <span />
              <span />
              <span />
            </span>
          </Navbar.Toggle>

          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto nav-links">
              <Nav.Link as={NavLink} to="/" className="nav-link-lg" end>
                Inicio
              </Nav.Link>
              <Nav.Link as={NavLink} to="/ligas" className="nav-link-lg">
                Ligas
              </Nav.Link>
              <Nav.Link as={NavLink} to="/partidos" className="nav-link-lg">
                Partidos
              </Nav.Link>
              <Nav.Link as={NavLink} to="/noticias" className="nav-link-lg">
                Noticias
              </Nav.Link>
              <Nav.Link as={NavLink} to="/suspensiones" className="nav-link-lg">
                Suspensiones
              </Nav.Link>
              <Nav.Link as={NavLink} to="/mis-equipos" className="nav-link-lg">
                Mis equipos
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="navbar-spacer" />
    </>
  );
};

export default MyNavbar;
